import type {
	OpaqueData,
	PaymentGatewayConfig,
	PaymentMethodFormData,
} from '../../types/payment.types';
import { PaymentGatewayError } from '../../types/payment.types';
import { ApiService } from '../ApiService';

// CyberSource Flex Microform types
interface FlexTokenOptions {
	expirationMonth?: string;
	expirationYear?: string;
}

interface FlexError {
	reason?: string;
	message?: string;
	details?: Array<{ location?: string; message?: string }>;
}

interface FlexFieldEvent {
	empty: boolean;
	valid: boolean;
	focused?: boolean;
	cardBrand?: string;
	cardBrandType?: string;
}

interface FlexField {
	on: (event: string, callback: (data: FlexFieldEvent) => void) => void;
	load: (containerId: string) => void;
}

interface FlexOptions {
	placeholder?: string;
	styles?: Record<string, Record<string, string>>;
}

interface FlexMicroform {
	createField: (fieldType: string, options?: FlexOptions) => FlexField;
	createToken: (
		options: FlexTokenOptions,
		callback: (error: FlexError | null, token: string | null) => void,
	) => void;
}

interface FlexInstance {
	microform: (
		type: string,
		options?: { styles?: Record<string, Record<string, string>> },
	) => FlexMicroform;
}

type PaymentContextType = 'CARD' | 'CHECK';

// Global Flex Microform interface (optional because it's dynamically loaded)
declare global {
	interface Window {
		Flex?: new (captureContext: string) => FlexInstance;
	}
}

export class CyberSourceFrontendService {
	private _config: PaymentGatewayConfig | null = null;
	private _scriptLoaded = false;

	// Card-specific fields
	private _cardCaptureContext: string | null = null;
	private _cardFlexInstance: FlexInstance | null = null;
	private _cardMicroform: FlexMicroform | null = null;
	private _cardNumberField: FlexField | null = null;
	private _securityCodeField: FlexField | null = null;
	private _isCardInitialized = false;
	private _detectedCardBrand: string | null = null;

	// ACH/Check-specific fields
	private _checkCaptureContext: string | null = null;
	private _checkFlexInstance: FlexInstance | null = null;
	private _checkMicroform: FlexMicroform | null = null;
	private _routingNumberField: FlexField | null = null;
	private _accountNumberField: FlexField | null = null;
	private _isCheckInitialized = false;

	// Static flag to track if preloading has been started
	private static _preloadStarted = false;

	/**
	 * Preload the Flex Microform script for faster initialization later.
	 * This can be called early (e.g., when we know CyberSource is the processor)
	 * to reduce the delay when the user opens the payment form.
	 */
	public static preloadScript(): void {
		if (CyberSourceFrontendService._preloadStarted) {
			return;
		}

		CyberSourceFrontendService._preloadStarted = true;

		const scriptUrl =
			'https://flex.cybersource.com/microform/bundle/v2/flex-microform.min.js';

		// Check if script already exists
		const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

		if (existingScript != null) {
			return;
		}

		// Add preload link hint for the browser
		const preloadLink = document.createElement('link');
		preloadLink.rel = 'preload';
		preloadLink.as = 'script';
		preloadLink.href = scriptUrl;
		document.head.appendChild(preloadLink);

		// Also start loading the script itself
		const script = document.createElement('script');
		script.src = scriptUrl;
		script.async = true;
		document.head.appendChild(script);
	}

	/**
	 * Initialize the service by loading configuration and Flex Microform SDK
	 * @param paymentType - Type of payment to initialize: 'CARD' for credit cards, 'CHECK' for ACH/eCheck
	 * @param externalConfig Optional pre-fetched gateway config. When provided,
	 *   skips the internal ApiService call — allows non-CRM consumers (e.g. portal)
	 *   to supply config fetched through their own API layer.
	 */
	public async initialize(
		paymentType: PaymentContextType = 'CARD',
		externalConfig?: PaymentGatewayConfig,
	): Promise<void> {
		// Check if already initialized for this payment type
		if (paymentType === 'CARD' && this._isCardInitialized) {
			return;
		}

		if (paymentType === 'CHECK' && this._isCheckInitialized) {
			return;
		}

		try {
			// 1. Get configuration — use external config if provided, otherwise fetch from backend
			if (this._config == null) {
				this._config = externalConfig ?? (await this._getConfiguration());
			}

			// Only initialize Flex if CyberSource is the processor
			if (this._config.processorType !== 'cybersource') {
				throw new PaymentGatewayError(
					'CyberSource is not the configured payment processor',
					'WRONG_PROCESSOR',
				);
			}

			// 2. Get capture context from backend for the specific payment type
			const captureContext = await this._getCaptureContext(paymentType);

			// 3. Load Flex Microform script
			await this._loadFlexScript();

			// 4. Initialize Flex instance (Flex is a class constructor)
			if (window.Flex == null) {
				throw new PaymentGatewayError(
					'Flex Microform script not loaded',
					'SCRIPT_NOT_LOADED',
				);
			}

			const flexInstance = new window.Flex(captureContext);

			// Store the instance based on payment type
			if (paymentType === 'CARD') {
				this._cardCaptureContext = captureContext;
				this._cardFlexInstance = flexInstance;
				this._isCardInitialized = true;
			} else {
				this._checkCaptureContext = captureContext;
				this._checkFlexInstance = flexInstance;
				this._isCheckInitialized = true;
			}
		} catch (error) {
			// Extract the actual error message for better debugging
			let errorMessage = 'Failed to initialize CyberSource service';

			if (error instanceof PaymentGatewayError) {
				errorMessage = error.message;
			} else if (error instanceof Error) {
				errorMessage = error.message;
			}

			throw new PaymentGatewayError(errorMessage, 'INIT_ERROR', error);
		}
	}

	/**
	 * Create and mount the secure card fields
	 * @param numberContainerId - ID of the container element for the card number field
	 * @param securityCodeContainerId - ID of the container element for the CVV field
	 * @param options - Optional configuration for styling and callbacks
	 */
	public createSecureCardFields(
		numberContainerId: string,
		securityCodeContainerId: string,
		options?: {
			onCardBrandChange?: (brand: string | null) => void;
			textColor?: string;
			placeholderColor?: string;
		},
	): void {
		if (this._cardFlexInstance == null) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		const textColor = options?.textColor ?? '#333';
		const placeholderColor = options?.placeholderColor ?? '#9CA3AF';

		// Styles for Flex Microform
		// Note: The ':hover' selector is required as a workaround for ::placeholder styles to work.
		// Without it, CyberSource Flex Microform ignores the placeholder color. (Support case pending)
		const customStyles = {
			input: {
				color: textColor,
			},
			'::placeholder': {
				color: placeholderColor,
			},
			':hover': {
				color: textColor,
			},
		};

		// Create the microform instance with styles
		this._cardMicroform = this._cardFlexInstance.microform('card', {
			styles: customStyles,
		});

		// Create card number field
		this._cardNumberField = this._cardMicroform.createField('number', {
			placeholder: 'Card Number',
		});

		// Listen for card brand changes
		this._cardNumberField.on('change', (data: FlexFieldEvent) => {
			if (data.cardBrand != null) {
				this._detectedCardBrand = this._mapCardBrand(data.cardBrand);

				if (options?.onCardBrandChange != null) {
					options.onCardBrandChange(this._detectedCardBrand);
				}
			}
		});

		// Create security code field
		this._securityCodeField = this._cardMicroform.createField('securityCode', {
			placeholder: 'CVV',
		});

		// Mount fields to containers
		this._cardNumberField.load(`#${numberContainerId}`);
		this._securityCodeField.load(`#${securityCodeContainerId}`);
	}

	/**
	 * Backwards compatibility alias for createSecureCardFields
	 * @deprecated Use createSecureCardFields instead
	 */
	public createSecureFields(
		numberContainerId: string,
		securityCodeContainerId: string,
		options?: {
			onCardBrandChange?: (brand: string | null) => void;
			textColor?: string;
			placeholderColor?: string;
		},
	): void {
		this.createSecureCardFields(
			numberContainerId,
			securityCodeContainerId,
			options,
		);
	}

	/**
	 * Create and mount the secure ACH/eCheck fields
	 * @param routingNumberContainerId - ID of the container element for the routing number field
	 * @param accountNumberContainerId - ID of the container element for the account number field
	 * @param options - Optional configuration for styling and callbacks
	 */
	public createSecureCheckFields(
		routingNumberContainerId: string,
		accountNumberContainerId: string,
		options?: {
			textColor?: string;
			placeholderColor?: string;
			onFieldChange?: (
				field: 'routingNumber' | 'accountNumber',
				event: FlexFieldEvent,
			) => void;
		},
	): void {
		if (this._checkFlexInstance == null) {
			throw new PaymentGatewayError(
				'Check service not initialized. Call initialize("CHECK") first.',
				'NOT_INITIALIZED',
			);
		}

		const textColor = options?.textColor ?? '#333';
		const placeholderColor = options?.placeholderColor ?? '#9CA3AF';

		// Styles for Flex Microform
		const customStyles = {
			input: {
				color: textColor,
			},
			'::placeholder': {
				color: placeholderColor,
			},
			':hover': {
				color: textColor,
			},
		};

		// Create the microform instance with 'check' type for eCheck/ACH
		this._checkMicroform = this._checkFlexInstance.microform('check', {
			styles: customStyles,
		});

		// Create routing number field
		this._routingNumberField = this._checkMicroform.createField(
			'routingNumber',
			{
				placeholder: 'Routing Number',
			},
		);

		// Listen for field changes
		this._routingNumberField.on('change', (data: FlexFieldEvent) => {
			if (options?.onFieldChange != null) {
				options.onFieldChange('routingNumber', data);
			}
		});

		// Create account number field
		this._accountNumberField = this._checkMicroform.createField(
			'accountNumber',
			{
				placeholder: 'Account Number',
			},
		);

		// Listen for field changes
		this._accountNumberField.on('change', (data: FlexFieldEvent) => {
			if (options?.onFieldChange != null) {
				options.onFieldChange('accountNumber', data);
			}
		});

		// Mount fields to containers
		this._routingNumberField.load(`#${routingNumberContainerId}`);
		this._accountNumberField.load(`#${accountNumberContainerId}`);
	}

	/**
	 * Tokenize credit card data using Flex Microform
	 */
	public async tokenizeCardData(
		formData: PaymentMethodFormData,
	): Promise<OpaqueData> {
		if (
			!this._isCardInitialized ||
			this._cardMicroform == null ||
			this._cardNumberField == null
		) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		// Parse expiration date - handles MMYY, MMYYYY, or MM[separator]YY/YYYY
		const expDate = formData.expirationDate;
		let expMonth: string;
		let expYearPart: string;

		if (/^\d{4}$/.test(expDate)) {
			// MMYY format (4 digits)
			expMonth = expDate.slice(0, 2);
			expYearPart = expDate.slice(2);
		} else if (/^\d{6}$/.test(expDate)) {
			// MMYYYY format (6 digits)
			expMonth = expDate.slice(0, 2);
			expYearPart = expDate.slice(2);
		} else {
			// MM[separator]YY or MM[separator]YYYY
			expMonth = expDate.slice(0, 2);
			expYearPart = expDate.replace(/^\d{2}\D/, '');
		}

		const expYear = expYearPart.length === 2 ? `20${expYearPart}` : expYearPart;

		// Create token using Flex Microform
		const microform = this._cardMicroform;

		return new Promise((resolve, reject) => {
			microform.createToken(
				{
					expirationMonth: expMonth,
					expirationYear: expYear,
				},
				(error: FlexError | null, token: string | null) => {
					if (error != null) {
						const errorMessage =
							error.message ??
							error.reason ??
							error.details?.[0]?.message ??
							'Unknown error occurred';

						console.error('[CyberSource] Token creation error:', error);

						reject(
							new PaymentGatewayError(
								`CyberSource error: ${errorMessage}`,
								'TOKEN_ERROR',
								error,
							),
						);

						return;
					}

					if (token == null) {
						console.error('[CyberSource] No token received');

						reject(
							new PaymentGatewayError(
								'No token received from CyberSource',
								'NO_TOKEN',
							),
						);

						return;
					}

					// Return opaque data in the same format as Authorize.net
					resolve({
						dataDescriptor: 'CYBERSOURCE_TRANSIENT_TOKEN',
						dataValue: token,
					});
				},
			);
		});
	}

	/**
	 * Tokenize eCheck/ACH bank account data using Flex Microform
	 * Uses the secure check fields to create a transient token
	 * @param _formData - Form data (not used directly as bank info comes from secure fields)
	 */
	public async tokenizeBankData(
		_formData: PaymentMethodFormData,
	): Promise<OpaqueData> {
		if (
			!this._isCheckInitialized ||
			this._checkMicroform == null ||
			this._routingNumberField == null ||
			this._accountNumberField == null
		) {
			throw new PaymentGatewayError(
				'Check service not initialized. Call initialize("CHECK") first.',
				'NOT_INITIALIZED',
			);
		}

		// Create token using Flex Microform for check/ACH
		const microform = this._checkMicroform;

		return new Promise((resolve, reject) => {
			// For check/ACH, we don't need to pass expiration info
			microform.createToken(
				{},
				(error: FlexError | null, token: string | null) => {
					if (error != null) {
						const errorMessage =
							error.message ??
							error.reason ??
							error.details?.[0]?.message ??
							'Unknown error occurred';

						console.error('[CyberSource] ACH Token creation error:', error);

						reject(
							new PaymentGatewayError(
								`CyberSource ACH error: ${errorMessage}`,
								'TOKEN_ERROR',
								error,
							),
						);

						return;
					}

					if (token == null) {
						console.error('[CyberSource] No ACH token received');

						reject(
							new PaymentGatewayError(
								'No ACH token received from CyberSource',
								'NO_TOKEN',
							),
						);

						return;
					}

					// Return opaque data with ACH-specific descriptor
					resolve({
						dataDescriptor: 'CYBERSOURCE_ACH_TRANSIENT_TOKEN',
						dataValue: token,
					});
				},
			);
		});
	}

	/**
	 * Get the detected card brand from the secure field
	 */
	public getDetectedCardBrand(): string | null {
		return this._detectedCardBrand;
	}

	/**
	 * Validate form data before tokenization
	 */
	public validateFormData(formData: PaymentMethodFormData): boolean {
		const errors: Array<string> = [];

		if (formData.paymentType === 'Credit Card') {
			// For credit cards, the card number and CVV are in secure fields
			// We only validate expiration date and name
			if (
				formData.expirationDate === '' ||
				!/^(\d{2}\D(\d{2}|\d{4})|\d{4}|\d{6})$/.test(formData.expirationDate)
			) {
				errors.push('Invalid expiration date (MM/YY)');
			}

			if (formData.nameOnCard.trim() === '') {
				errors.push('Name on card is required');
			}
		} else if (formData.paymentType === 'ACH') {
			// eCheck validation
			// When using Flex Microform secure fields, routing and account numbers
			// are in secure iframes, so we skip validating them here
			if (!this._isCheckInitialized) {
				// Only validate routing/account if NOT using secure fields
				if (
					formData.routingNumber === '' ||
					!/^\d{9}$/.test(formData.routingNumber)
				) {
					errors.push('Routing number must be exactly 9 digits');
				}

				if (
					formData.accountNumber.trim() === '' ||
					formData.accountNumber.trim().length < 4
				) {
					errors.push('Valid account number is required');
				}
			}

			if (formData.nameOnAccount.trim() === '') {
				errors.push('Name on account is required');
			}

			if (!['checking', 'savings'].includes(formData.accountType)) {
				errors.push('Valid account type (checking or savings) is required');
			}
		}

		// Common validation
		if (formData.zipCode.trim() === '') {
			errors.push('ZIP code is required');
		}

		if (errors.length > 0) {
			throw new PaymentGatewayError(
				`Validation failed: ${errors.join(', ')}`,
				'VALIDATION_ERROR',
			);
		}

		return true;
	}

	/**
	 * Check if service is initialized for a specific payment type
	 */
	public isInitialized(paymentType: PaymentContextType = 'CARD'): boolean {
		if (paymentType === 'CARD') {
			return this._isCardInitialized;
		}

		return this._isCheckInitialized;
	}

	/**
	 * Clean up resources for a specific payment type or all
	 */
	public cleanup(paymentType?: PaymentContextType): void {
		if (paymentType == null || paymentType === 'CARD') {
			// Clear card-specific data
			this._cardCaptureContext = null;
			this._cardFlexInstance = null;
			this._cardMicroform = null;
			this._cardNumberField = null;
			this._securityCodeField = null;
			this._detectedCardBrand = null;
			this._isCardInitialized = false;
		}

		if (paymentType == null || paymentType === 'CHECK') {
			// Clear check/ACH-specific data
			this._checkCaptureContext = null;
			this._checkFlexInstance = null;
			this._checkMicroform = null;
			this._routingNumberField = null;
			this._accountNumberField = null;
			this._isCheckInitialized = false;
		}

		// Only clear config if cleaning up everything
		if (paymentType == null) {
			this._config = null;
		}
	}

	/**
	 * Get configuration from backend
	 */
	private async _getConfiguration(): Promise<PaymentGatewayConfig> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: PaymentGatewayConfig;
				error?: string;
			}>('/payments/config/public');

			if (!response.success || response.data == null) {
				throw new Error(response.error ?? 'No configuration received');
			}

			return response.data;
		} catch (error) {
			throw new PaymentGatewayError(
				'Failed to fetch payment gateway configuration',
				'CONFIG_ERROR',
				error,
			);
		}
	}

	/**
	 * Get capture context from backend for Flex Microform
	 * @param paymentType - Type of payment: 'CARD' for credit cards, 'CHECK' for ACH/eCheck
	 */
	private async _getCaptureContext(
		paymentType: PaymentContextType = 'CARD',
	): Promise<string> {
		try {
			// Get current origin for the target origins
			const targetOrigins = [window.location.origin];

			const response = await ApiService.apiPost<{
				success: boolean;
				data?: { captureContext: string };
				error?: string;
			}>('/payments/config/cybersource/capture-context', {
				targetOrigins,
				paymentType,
			});

			if (!response.success || response.data?.captureContext == null) {
				throw new Error(response.error ?? 'No capture context received');
			}

			return response.data.captureContext;
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'Failed to get CyberSource capture context';

			throw new PaymentGatewayError(
				errorMessage,
				'CAPTURE_CONTEXT_ERROR',
				error,
			);
		}
	}

	/**
	 * Load Flex Microform script dynamically
	 */
	private async _loadFlexScript(): Promise<void> {
		if (this._scriptLoaded && window.Flex != null) {
			return;
		}

		// Flex Microform SDK URL
		const scriptUrl =
			'https://flex.cybersource.com/microform/bundle/v2/flex-microform.min.js';

		return new Promise((resolve, reject) => {
			// Check if script already exists (possibly from preloading)
			const existingScript = document.querySelector(
				`script[src="${scriptUrl}"]`,
			);

			// If Flex is already available, we're done
			if (window.Flex != null) {
				this._scriptLoaded = true;
				resolve();

				return;
			}

			// If script exists but Flex isn't ready yet, wait for it
			if (existingScript != null) {
				const checkFlex = (): void => {
					if (window.Flex != null) {
						this._scriptLoaded = true;
						resolve();
					} else {
						// Keep checking until Flex is available (max 10 seconds)
						setTimeout(checkFlex, 100);
					}
				};

				// Start checking for Flex availability
				checkFlex();

				return;
			}

			// Script doesn't exist, create it
			const script = document.createElement('script');
			script.src = scriptUrl;
			script.async = true;

			script.onload = (): void => {
				this._scriptLoaded = true;

				// Give Flex time to fully initialize
				setTimeout(() => {
					if (window.Flex != null) {
						resolve();
					} else {
						reject(
							new PaymentGatewayError(
								'Flex Microform did not initialize properly',
								'SCRIPT_INIT_ERROR',
							),
						);
					}
				}, 100);
			};

			script.onerror = (): void => {
				reject(
					new PaymentGatewayError(
						'Failed to load Flex Microform script',
						'SCRIPT_LOAD_ERROR',
					),
				);
			};

			document.head.appendChild(script);
		});
	}

	/**
	 * Map CyberSource card brand to internal format
	 */
	private _mapCardBrand(cyberSourceBrand: string): string {
		const brandMap: Record<string, string> = {
			VISA: 'Visa',
			visa: 'Visa',
			MASTERCARD: 'MasterCard',
			mastercard: 'MasterCard',
			AMEX: 'Amex',
			amex: 'Amex',
			AMERICANEXPRESS: 'Amex',
			americanexpress: 'Amex',
			DISCOVER: 'Discover',
			discover: 'Discover',
			DINERS_CLUB: 'Diners Club',
			dinersclub: 'Diners Club',
			JCB: 'JCB',
			jcb: 'JCB',
		};

		return brandMap[cyberSourceBrand] ?? cyberSourceBrand;
	}
}
